import { GetGeographicRegionListingSchema } from '../schemas/GeographicRegionZodSchema';
import { GeographicRegion } from '../types/GeographicRegion';
import { Country } from '../types/Country';
import { CountryHubspotMappingForYageoContactUs } from '../types/countryHubspotMapping';
import { SubnationalRegionHubspotMappingForYageoContactUs } from '../types/subnationalRegionHubspotMapping';
import { SubnationalRegion } from '../types/SubnationalRegion';
import { CountrySchema, SubnationalRegionSchema } from '../schemas/GeographicRegionZodSchema';
import { z } from 'zod';

function makeYageoContactUsCountryMapping(
    country: z.infer<typeof CountrySchema>
): CountryHubspotMappingForYageoContactUs {
    return country.hubspotYageoContactUs_enabled
        ? {
              enabled: true as const,
              fieldValue: country.hubspotYageoContactUs_fieldValue!,
              subnationalRegionFieldName: country.hubspotYageoContactUs_subregionFieldName ?? null,
          }
        : {
              enabled: false as const,
              fieldValue: country.hubspotYageoContactUs_fieldValue ?? null,
              subnationalRegionFieldName: country.hubspotYageoContactUs_subregionFieldName ?? null,
          };
}

function makeYageoContactUsSubnationalRegionMapping(
    subregion: z.infer<typeof SubnationalRegionSchema>
): SubnationalRegionHubspotMappingForYageoContactUs {
    return subregion.hubspotYageoContactUs_enabled
        ? {
              enabled: true as const,
              fieldValue: subregion.hubspotYageoContactUs_fieldValue!,
          }
        : {
              enabled: false as const,
              fieldValue: subregion.hubspotYageoContactUs_fieldValue ?? null,
          };
}

/**
 * Transforms and validates the repsonse from the getGeographicRegionListing GraphQL query to a
 * simplified GeographicRegion, Country, and SubnationalRegion types.
 */
export function transformGeographicRegionResponse(
    data?: unknown // `unknown` is used here for jest tests to be able to pass invalid data
): {
    geographicRegions: GeographicRegion[];
    countries: Country[];
    subnationalRegions: SubnationalRegion[];
} {
    // Parse and validate the data using the Zod schema
    const parsedResult = GetGeographicRegionListingSchema.safeParse(data);

    if (!parsedResult.success) {
        console.error(
            'Validation failed for getGeographicRegionListing:',
            parsedResult.error.errors
        );
        return {
            geographicRegions: [],
            countries: [],
            subnationalRegions: [],
        };
    }

    const parsedRegions = parsedResult.data.getObjectFolder.children;

    const geographicRegions: GeographicRegion[] = [];
    const countries: Country[] = [];
    const subnationalRegions: SubnationalRegion[] = [];

    // Build relationships
    parsedRegions.forEach((parsedRegion) => {
        const region: GeographicRegion = {
            id: parsedRegion.id,
            key: parsedRegion.key,
            name: parsedRegion.name,
            countries: [],
        };

        parsedRegion.countries.forEach((parsedCountry) => {
            const yageoContactUs: CountryHubspotMappingForYageoContactUs =
                makeYageoContactUsCountryMapping(parsedCountry);
            const country: Country = {
                id: parsedCountry.id,
                key: parsedCountry.key,
                name: parsedCountry.name,
                geographicRegion: region,
                hubspot: { yageoContactUs },
                subnationalRegionType: parsedCountry.subnationalRegionType
                    ? {
                          id: parsedCountry.subnationalRegionType.id,
                          key: parsedCountry.subnationalRegionType.key,
                          name: parsedCountry.subnationalRegionType.name,
                          selectionPrompt: parsedCountry.subnationalRegionType.selectionPrompt,
                      }
                    : null,
                subnationalRegions: [],
            };

            parsedCountry.subnationalRegions.forEach((parsedSubregion) => {
                const yageoContactUs: SubnationalRegionHubspotMappingForYageoContactUs =
                    makeYageoContactUsSubnationalRegionMapping(parsedSubregion);
                const subregion: SubnationalRegion = {
                    id: parsedSubregion.id,
                    key: parsedSubregion.key,
                    name: parsedSubregion.name,
                    hubspot: { yageoContactUs },
                    country: country,
                };
                subnationalRegions.push(subregion);
                country.subnationalRegions.push(subregion);
            });

            countries.push(country);
            region.countries.push(country);
        });

        geographicRegions.push(region);
    });

    return { geographicRegions, countries, subnationalRegions };
}
